import { useAuth0 } from '@auth0/auth0-react'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Loader,
  Banner,
  Breadcrumbs,
  Table,
  LayerCard,
  Empty,
  Badge,
  Button,
  useKumoToastManager,
} from '@cloudflare/kumo'
import { WifiHighIcon, HouseIcon, PlusIcon, PencilSimpleIcon } from '@phosphor-icons/react'
import { apiRequest, ApiError } from '../api/client'
import { useHasRole } from '../auth/useRoles'
import { useTranslation } from '../i18n'
import { PageHeader } from '../components/kumo/page-header/page-header'
import { DeleteResource } from '../components/kumo/delete-resource/delete-resource'
import type { WifiNetwork, WifiSecurityType, WifiNetworkType } from '@hub-wifi/shared'

// Maps enum values to i18n keys — mirrors the PrinterStatusBadge pattern
// (single source of truth for enum -> label); also used by the Select
// options on WifiNetworkFormPage.
const SECURITY_TYPE_KEY: Record<WifiSecurityType, string> = {
  Open: 'wifiNetworks.securityType.open',
  WPA2: 'wifiNetworks.securityType.wpa2',
  'WPA2/WPA3 Enterprise': 'wifiNetworks.securityType.wpa2wpa3Enterprise',
}

const NETWORK_TYPE_KEY: Record<WifiNetworkType, string> = {
  internal: 'wifiNetworks.networkType.internal',
  coworkers: 'wifiNetworks.networkType.coworkers',
  guests: 'wifiNetworks.networkType.guests',
}

type NetworkTab = 'all' | WifiNetworkType

export function WifiNetworksPage() {
  const { getAccessTokenSilently } = useAuth0()
  const { t } = useTranslation()
  const isAdmin = useHasRole('admin')
  const navigate = useNavigate()
  const toasts = useKumoToastManager()
  const [networks, setNetworks] = useState<WifiNetwork[]>([])
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<NetworkTab>('all')
  const [deleteTarget, setDeleteTarget] = useState<WifiNetwork | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  const loadNetworks = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently()
      const data = await apiRequest<WifiNetwork[]>('/api/wifi-networks', { token })
      setNetworks(data)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'GENERIC_ERROR')
    } finally {
      setLoading(false)
    }
  }, [getAccessTokenSilently])

  useEffect(() => {
    void loadNetworks()
  }, [loadNetworks])

  async function handleDelete(network: WifiNetwork) {
    try {
      setIsDeleting(true)
      const token = await getAccessTokenSilently()
      await apiRequest(`/api/wifi-networks/${network.id}`, {
        method: 'DELETE',
        token,
      })
      toasts.add({ variant: 'success', title: t('wifiNetworks.deleteSuccess') })
      setDeleteTarget(null)
      setLoading(true)
      await loadNetworks()
    } catch (err) {
      toasts.add({
        variant: 'error',
        title: t('common.errorTitle'),
        description: err instanceof ApiError ? err.message : t('common.error'),
      })
    } finally {
      setIsDeleting(false)
    }
  }

  const filteredNetworks =
    activeTab === 'all' ? networks : networks.filter((network) => network.networkType === activeTab)

  return (
    <div>
      <PageHeader
        breadcrumbs={
          <Breadcrumbs>
            <Breadcrumbs.Link icon={<HouseIcon size={14} />} href="/">
              {t('nav.home')}
            </Breadcrumbs.Link>
            <Breadcrumbs.Separator />
            <Breadcrumbs.Current>{t('wifiNetworks.title')}</Breadcrumbs.Current>
          </Breadcrumbs>
        }
        title={t('wifiNetworks.title')}
        description={t('wifiNetworks.description')}
        tabs={[
          { label: t('wifiNetworks.tabs.all'), value: 'all' },
          { label: t(NETWORK_TYPE_KEY.internal), value: 'internal' },
          { label: t(NETWORK_TYPE_KEY.coworkers), value: 'coworkers' },
          { label: t(NETWORK_TYPE_KEY.guests), value: 'guests' },
        ]}
        defaultTab={activeTab}
        onValueChange={(value) => setActiveTab(value as NetworkTab)}
      >
        {isAdmin && (
          <Button
            variant="primary"
            size="sm"
            icon={<PlusIcon size={16} />}
            onClick={() => navigate('/wifi-networks/new')}
          >
            {t('wifiNetworks.addNetwork')}
          </Button>
        )}
      </PageHeader>

      {loading && (
        <div className="flex items-center justify-center py-16">
          <Loader size="lg" />
        </div>
      )}

      {!loading && error && (
        <Banner variant="error">
          {t('common.errorPrefix')} {error === 'GENERIC_ERROR' ? t('common.error') : error}
        </Banner>
      )}

      {!loading && !error && networks.length === 0 && (
        <Empty
          icon={<WifiHighIcon size={48} className="text-kumo-inactive" />}
          title={t('wifiNetworks.empty')}
        />
      )}

      {!loading && !error && networks.length > 0 && filteredNetworks.length === 0 && (
        <Empty
          icon={<WifiHighIcon size={48} className="text-kumo-inactive" />}
          title={t('common.noResultsForFilter')}
        />
      )}

      {!loading && !error && filteredNetworks.length > 0 && (
        <LayerCard>
          <LayerCard.Primary className="p-0">
            <div style={{ overflowX: 'auto', width: '100%' }}>
              <Table>
                <Table.Header>
                  <Table.Row>
                    <Table.Head>{t('wifiNetworks.name')}</Table.Head>
                    <Table.Head>{t('wifiNetworks.vlanId')}</Table.Head>
                    <Table.Head>{t('wifiNetworks.securityType')}</Table.Head>
                    <Table.Head>{t('wifiNetworks.networkType')}</Table.Head>
                    <Table.Head>{t('wifiNetworks.createdAt')}</Table.Head>
                    {isAdmin && <Table.Head>{t('wifiNetworks.actions')}</Table.Head>}
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {filteredNetworks.map((network) => (
                    <Table.Row key={network.id}>
                      <Table.Cell>{network.name}</Table.Cell>
                      <Table.Cell>{network.vlanId}</Table.Cell>
                      <Table.Cell>
                        <Badge variant="secondary">
                          {t(SECURITY_TYPE_KEY[network.securityType])}
                        </Badge>
                      </Table.Cell>
                      <Table.Cell>
                        <Badge variant="primary">{t(NETWORK_TYPE_KEY[network.networkType])}</Badge>
                      </Table.Cell>
                      <Table.Cell>{new Date(network.createdAt).toLocaleDateString()}</Table.Cell>
                      {isAdmin && (
                        <Table.Cell>
                          <div className="flex gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              icon={<PencilSimpleIcon size={16} />}
                              onClick={() => navigate(`/wifi-networks/${network.id}/edit`)}
                            >
                              {t('wifiNetworks.edit')}
                            </Button>
                            <Button
                              variant="secondary-destructive"
                              size="sm"
                              onClick={() => setDeleteTarget(network)}
                            >
                              {t('wifiNetworks.delete')}
                            </Button>
                          </div>
                        </Table.Cell>
                      )}
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </div>
          </LayerCard.Primary>
        </LayerCard>
      )}

      <DeleteResource
        open={!!deleteTarget}
        onOpenChange={(next) => {
          if (!next) setDeleteTarget(null)
        }}
        resourceType={t('deleteResource.type.wifiNetwork')}
        resourceName={deleteTarget?.name ?? ''}
        isDeleting={isDeleting}
        onDelete={() => (deleteTarget ? handleDelete(deleteTarget) : undefined)}
      />
    </div>
  )
}
